import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Clock,
  Lightning,
  PencilSimple,
  Plus,
  Target,
  Trash,
  UsersThree
} from '@phosphor-icons/react';

const ProcessVisualView = ({ process, steps, onStepUpdate, onStepDelete, onStepCreate, onAddStepClick }) => {
  const [editingStep, setEditingStep] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [insertAfterIndex, setInsertAfterIndex] = useState(-1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    actor_role: '',
    system: '',
    avg_time_minutes: '',
    manual_or_automated: 'manual',
    pain_points: []
  });

  const handleEdit = (step) => {
    setEditingStep(step.id);
    setFormData({
      title: step.title || '',
      description: step.description || '',
      actor_role: step.actor_role || '',
      system: step.system || '',
      avg_time_minutes: step.avg_time_minutes || '',
      manual_or_automated: step.manual_or_automated || 'manual',
      pain_points: step.pain_points || []
    });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a step title');
      return;
    }

    await onStepUpdate(editingStep, formData);
    setEditingStep(null);
  };

  const handleCancel = () => {
    setEditingStep(null);
    setFormData({
      title: '',
      description: '',
      actor_role: '',
      system: '',
      avg_time_minutes: '',
      manual_or_automated: 'manual',
      pain_points: []
    });
  };

  const handleAddStepClick = (afterIndex) => {
    setInsertAfterIndex(afterIndex);
    setFormData({
      title: '',
      description: '',
      actor_role: '',
      system: '',
      avg_time_minutes: '',
      manual_or_automated: 'manual',
      pain_points: []
    });
    setShowAddModal(true);
  };

  const handleCreateStep = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a step title');
      return;
    }

    // Calculate order_index: insert after the specified index
    const order_index = insertAfterIndex === -1 ? 1 : insertAfterIndex + 2;

    await onStepCreate({
      ...formData,
      order_index
    });

    setShowAddModal(false);
    setFormData({
      title: '',
      description: '',
      actor_role: '',
      system: '',
      avg_time_minutes: '',
      manual_or_automated: 'manual',
      pain_points: []
    });
  };

  return (
    <div className="relative">
      {/* Note: Add Step button is now in the toolbar (ProcessDetail.js) */}
      {/* This keeps the UI consistent across List and Visual views */}

      {/* Steps Flow */}
      <div className="space-y-6">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step Node */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="relative"
            >
              {/* Vertical connector from previous step */}
              {index > 0 && (
                <div className="absolute left-1/2 -translate-x-1/2 -top-6 w-0.5 h-6 bg-gradient-to-b from-[#6366F1] to-[#EC4899]" />
              )}

              {/* Step Card */}
              <div className="relative glass rounded-2xl p-6 hover:shadow-[0_0_30px_rgba(99,102,241,0.3)] transition-all">
                {editingStep === step.id ? (
                  /* PencilSimple Mode */
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                      placeholder="Step Title"
                    />
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] resize-none"
                      rows={2}
                      placeholder="Description"
                    />
                    <div className="grid md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        value={formData.actor_role}
                        onChange={(e) => setFormData({ ...formData, actor_role: e.target.value })}
                        className="px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                        placeholder="Actor Role"
                      />
                      <input
                        type="text"
                        value={formData.system}
                        onChange={(e) => setFormData({ ...formData, system: e.target.value })}
                        className="px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                        placeholder="System"
                      />
                      <input
                        type="number"
                        value={formData.avg_time_minutes}
                        onChange={(e) => setFormData({ ...formData, avg_time_minutes: e.target.value })}
                        className="px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                        placeholder="Time (min)"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                        <h3 className="text-xl font-bold text-white">{step.title}</h3>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(step)}
                          className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white"
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => onStepDelete(step.id)}
                          className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-[#EF4444]"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                    {step.description && (
                      <p className="text-[#A0AEC0] mb-4">{step.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm">
                      {step.actor_role && (
                        <span className="flex items-center gap-1 text-[#A0AEC0]">
                          <UsersThree size={14} />
                          {step.actor_role}
                        </span>
                      )}
                      {step.system && (
                        <span className="flex items-center gap-1 text-[#A0AEC0]">
                          <Target size={14} />
                          {step.system}
                        </span>
                      )}
                      {step.avg_time_minutes && (
                        <span className="flex items-center gap-1 text-[#A0AEC0]">
                          <Clock size={14} />
                          {step.avg_time_minutes} min
                        </span>
                      )}
                      {step.manual_or_automated && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          step.manual_or_automated === 'automated' ? 'bg-[#14F195]/20 text-[#14F195]' :
                          step.manual_or_automated === 'semi-automated' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                          'bg-[#A0AEC0]/20 text-[#A0AEC0]'
                        }`}>
                          {step.manual_or_automated}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>

            {/* Add Step After Button */}
            {!editingStep && (
              <div className="flex justify-center">
                <button
                  onClick={() => handleAddStepClick(index)}
                  className="p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white transition-all"
                  title="Add step after this one"
                >
                  <Plus size={16} />
                </button>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Add Step Modal - Rendered via Portal at document root */}
      {showAddModal && createPortal(
        <AnimatePresence>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="glass rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-2xl font-bold text-white mb-6">
                {insertAfterIndex === -1 ? 'Add Step at Start' : `Add Step After Step ${insertAfterIndex + 1}`}
              </h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                  placeholder="Step Title *"
                />
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] resize-none"
                  rows={3}
                  placeholder="Description"
                />
                <div className="grid md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    value={formData.actor_role}
                    onChange={(e) => setFormData({ ...formData, actor_role: e.target.value })}
                    className="px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    placeholder="Actor Role"
                  />
                  <input
                    type="text"
                    value={formData.system}
                    onChange={(e) => setFormData({ ...formData, system: e.target.value })}
                    className="px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    placeholder="System"
                  />
                  <input
                    type="number"
                    value={formData.avg_time_minutes}
                    onChange={(e) => setFormData({ ...formData, avg_time_minutes: e.target.value })}
                    className="px-4 py-3 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)]"
                    placeholder="Time (min)"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateStep}
                    className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold"
                  >
                    Add Step
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </AnimatePresence>,
        document.body
      )}

      {/* Empty State - Clean single CTA */}
      {steps.length === 0 && (
        <div className="text-center py-16">
          <div className="mb-6">
            <Lightning size={48} className="mx-auto text-[#6366F1] opacity-50" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No steps yet</h3>
          <p className="text-[#A0AEC0] mb-6">Add your first step to start mapping this process</p>
          <button
            onClick={onAddStepClick}
            className="relative overflow-hidden rounded-full group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
            <span className="relative z-10 flex items-center gap-2 px-6 py-3 text-white font-bold">
              <Plus size={20} />
              Add First Step
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProcessVisualView;
